import { createCrudPage } from './factories/crud-page.js';

export default createCrudPage({
  title: 'Countries',
  basePath: '/countries',
  reorderable: true,
  columns: [
    { key: 'orderby', label: 'Order' },
    { key: 'country', label: 'Country' },
    { key: 'status', label: 'Status',
      render: (v) => v == 1 ? 'Production' : 'Test' },
    { key: 'code', label: 'Code' },
    { key: 'url', label: 'URL' },
    { key: 'modules_list', label: 'Modules' },
    { key: 'expo_regions_list', label: 'Expo Regions' },
  ],
  fieldSections: [
    { key: 'general', label: 'General' },
    { key: 'website', label: 'Website' },
    { key: 'payments', label: 'Payments' },
    { key: 'modules', label: 'Modules' },
    { key: 'social', label: 'Social & Analytics' },
    { key: 'seo', label: 'SEO' },
    { key: 'video', label: 'Video' },
  ],
  fields: [
    // --- General ---
    { key: 'status', label: 'Country status', type: 'radio', section: 'general',
      options: [
        { value: '1', label: 'Production' },
        { value: '0', label: 'Test' },
      ], defaultValue: '0' },
    { key: 'country', label: 'Country', type: 'text', required: true, section: 'general' },
    { key: 'country_en', label: 'Country (Eng)', type: 'text', section: 'general' },
    { key: 'code', label: 'Country Code', type: 'text', section: 'general' },
    { key: 'db_country', label: 'Database country code', type: 'text', section: 'general' },
    { key: 'lang_code', label: 'Language', type: 'async-select', section: 'general',
      optionsUrl: '/languages/options',
      placeholder: 'Select language' },
    { key: 'text_direction', label: 'Text direction', type: 'radio', section: 'general',
      options: [
        { value: 'ltr', label: 'LTR' },
        { value: 'rtl', label: 'RTL' },
      ], defaultValue: 'ltr' },
    { key: 'date_format', label: 'Date format', type: 'radio', section: 'general',
      options: [
        { value: 'd.m.Y', label: 'dd.mm.YYYY' },
        { value: 'd.m.Y.', label: 'dd.mm.YYYY.' },
        { value: 'Y.m.d', label: 'YYYY.mm.dd' },
        { value: 'Y.m.d.', label: 'YYYY.mm.dd.' },
        { value: 'Y-m-d', label: 'YYYY-mm-dd' },
        { value: 'm.d.Y', label: 'mm.dd.YYYY' },
      ], defaultValue: 'd.m.Y' },

    // --- Website ---
    { key: 'title_in_country', label: 'Title in country (used on home page)', type: 'text', section: 'website' },
    { key: 'img_country', label: 'Items country code', type: 'text', section: 'website' },
    { key: 'img_url', label: 'Itemserver url', type: 'text', section: 'website' },
    { key: 'pager', label: 'Pager default', type: 'text', section: 'website' },
    { key: 'currency', label: 'Country currency', type: 'text', section: 'website' },
    { key: 'url', label: 'Platform URL', type: 'text', section: 'website' },
    { key: 'title', label: 'Portal Title', type: 'text', section: 'website' },
    { key: 'browser_title', label: 'Title in browser', type: 'text', section: 'website',
      fieldInfo: 'Title visible in a browser' },
    { key: 'email', label: 'Portal Email', type: 'text', section: 'website' },
    { key: 'forum_admin_email', label: 'Forum admin email', type: 'text', section: 'website' },
    { key: 'forum_comments', label: 'Forum allow comments', type: 'yesno', defaultValue: '1', section: 'website' },
    { key: 'bookstore_email', label: 'Bookstore Email', type: 'text', section: 'website' },

    // --- Payments ---
    { key: 'card_provider', label: 'Bookstore Card Provider', type: 'select', section: 'payments',
      options: [
        { value: '0', label: 'No provider' },
        { value: 'payU', label: 'payU' },
      ], defaultValue: '0' },
    { key: 'card_provider_status', label: 'Bookstore Card Provider Status', type: 'select', section: 'payments',
      options: [
        { value: '0', label: 'Not active' },
        { value: '1', label: 'Production' },
        { value: '2', label: 'Sandbox' },
      ], defaultValue: '0' },

    // --- Modules ---
    { key: 'modules', label: 'Additional modules', type: 'multi-relation', section: 'modules',
      engine: 'searchable-multi', optionsUrl: '/modules/options' },
    { key: 'expo_regions', label: 'Expo regions', type: 'multi-relation', section: 'modules',
      engine: 'searchable-multi', optionsUrl: '/expo-regions/options' },
    { key: 'article_sections', label: 'Home page news sections', type: 'multi-relation', section: 'modules',
      engine: 'searchable-multi', optionsUrl: '/article-sections/options', valueProp: 'pid' },

    // --- Social & Analytics ---
    { key: 'fb_link', label: 'Facebook link', type: 'text', section: 'social' },
    { key: 'fb_app_id', label: 'Facebook app ID', type: 'text', section: 'social' },
    { key: 'twitter_link', label: 'Twitter link', type: 'text', section: 'social' },
    { key: 'linkedin_link', label: 'LinkedIn', type: 'text', section: 'social' },
    { key: 'youtube_link', label: 'You Tube', type: 'text', section: 'social' },
    { key: 'instagram_link', label: 'Instagram', type: 'text', section: 'social' },
    { key: 'google_analytics', label: 'Google analytics ID', type: 'text', section: 'social' },
    { key: 'google_analytics4', label: 'Google analytics4 ID', type: 'text', section: 'social',
      fieldInfo: 'Insert Measurment ID, located in Data Streams. Something like: G-EPBJYZJX1V' },
    { key: 'google_tag', label: 'Google Tag Manager ID', type: 'text', section: 'social',
      fieldInfo: 'Insert only Google Tag manager ID. Something like: GTM-5RQG72' },
    { key: 'fb_pixel', label: 'Facebook Pixel ID', type: 'text', section: 'social' },
    { key: 'googlenews_link', label: 'Google news link', type: 'text', section: 'social' },
    { key: 'zone', label: 'Zone ID', type: 'text', section: 'social',
      fieldInfo: 'Used for default top banner' },
    { key: 'google_search_partner_pub', label: 'Google Search Partner Pub', type: 'text', section: 'social' },

    // --- SEO ---
    { key: 'meta_description', label: 'Meta description', type: 'textarea', section: 'seo' },
    { key: 'meta_keywords', label: 'Meta keywords', type: 'textarea', section: 'seo' },
    { key: 'meta_abstract', label: 'Meta abstract', type: 'text', section: 'seo' },
    { key: 'meta_short', label: 'Meta short', type: 'text', section: 'seo' },
    { key: 'meta_author', label: 'Meta author', type: 'text', section: 'seo' },
    { key: 'meta_classification', label: 'Meta classification', type: 'text', section: 'seo' },

    // --- Video ---
    { key: 'videos_number_hp', label: 'Number of videos on Home page', type: 'text', section: 'video',
      fieldInfo: 'Minimum is 4' },
    { key: 'currency_iso4217', label: 'Currency code currency_iso4217', type: 'text', section: 'video' },
    { key: 'video_channel', label: 'YouTube channel url', type: 'text', section: 'video' },
    { key: 'video_channel_background', label: 'Screen shot', type: 'file', section: 'video',
      accept: 'image/*', photo: true,
      fieldInfo: 'This photo will be used on Home Page, video module' },
  ],
});
